import {inject, Injectable} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {DocumentStore} from '../stores/document.store';
import {map} from 'rxjs/operators';
import {Document} from '../api/models/document';
import {Vector2D} from '../models/vector-2d.model';
import {EntityQueryService} from '../modules/akita-mimic/services/entity-query.service';
import {DocumentState} from '../modules/akita-mimic/models/store-states/document-state.model';
import {SortOrder} from '../modules/akita-mimic/enums/sort-order.enum';
import {DocumentViewModeEnum} from '../enums/document-view-mode.enum';

@Injectable({ providedIn: 'root' })
export class DocumentQuery extends EntityQueryService<DocumentState, Document> {
    public loading$: Observable<boolean> = this.select('loading');
    public currentPreviewPage$: Observable<number> = this.select('currentPreviewPage');
    public documentViewMode$: Observable<DocumentViewModeEnum> = this.select('documentViewMode');
    public loadingErrorSelectedDocumentId$: Observable<string | undefined> = this.select('loadingErrorSelectedDocumentId');
    public selectedDocument$: Observable<Document | undefined>;
    private readonly selectedDocumentId$: Observable<string | undefined> = this.select('selected');

    public constructor() {
        super(inject(DocumentStore), {
            sortBy: 'name',
            sortByOrder: SortOrder.ASC,
        });

        this.selectedDocument$ = combineLatest([
            this.selectAll(),
            this.selectedDocumentId$
        ])
            .pipe(map(() => this.getSelectedDocument()));
    }

    public getSelectedDocument(): Document | undefined {
        const selectedId = this.getValue().selected;
        if (selectedId && this.hasEntity(selectedId)) {
            return this.getEntity(selectedId);
        }
        return undefined;
    }

    public getSelectedDocumentId(): string | undefined {
        return this.getValue().selected;
    }

    public getDocumentById(docId: string): Document | undefined {
        const entities = this.getValue().entities;
        if (entities) {
            return Object.values(entities)
                .find(d => d.id === docId);
        }
        return undefined;
    }

    public getDocumentViewMode(): DocumentViewModeEnum {
        return this.getValue().documentViewMode;
    }

    public getImageSize(): Vector2D {
        const values = this.getValue();
        return {
            x: values.imageSize.x,
            y: values.imageSize.y
        };
    }
}
